import { Settings } from "../../settings/Settings";
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { Env } from "../../settings/Env";
import { BaseRoomMenu } from "./BaseRoomMenu";
import { Mesh2D } from "@dreamcam/babylon-tools";
import { createVector3 } from "../ButtonAnimation";
import { MeshName } from "../MeshName";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { VideoClipMenuMain } from "./VideoClipMenuMain";
import { VideoClipMenuRecommended } from "./VideoClipMenuRecommended";
import { VideoClipInfoPanel } from "./VideoClipInfoPanel";
import { VideoClipHelpPanel } from "./VideoClipHelpPanel";
import { AppView } from "../AppView";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode.js";
const deg2rad = Math.PI / 180;
const commonExcludedMeshNames = [
    MeshName.progressBar,
    MeshName.notificationCenter,
];
const excludedInfoPanelMeshNames = [
    ...commonExcludedMeshNames,
    MeshName.videoMenuInfoPanel,
];
const excludedHelpPanelMeshNames = [
    ...commonExcludedMeshNames,
    MeshName.videoMenuHelpPanel,
];
const excludedLoginDialogMeshNames = [
    ...commonExcludedMeshNames,
    MeshName.loginForm,
    MeshName.loginFormKeyboard,
];
const excludedMessengerPanelMeshNames = [
    ...commonExcludedMeshNames,
    MeshName.videoClipMessengerPanel,
    MeshName.videoClipMessengerChatListPanel,
    MeshName.videoClipMessengerEllipsisSubmenu,
    MeshName.videoClipMessengerLinkWarningDialog,
    MeshName.videoClipChatKeyboard,
    MeshName.hideVideoClipChatKeyboard,
    MeshName.videoClipMessengerTipsKeyboardPanel,
];
export class VideoClipMenu extends BaseRoomMenu {
    constructor(callbacks, messengerCallbacks, videoClipMenuCallbacks, projectionMeshCallbacks) {
        super(callbacks, messengerCallbacks);
        this._videoClip = null;
        this._isActive = true;
        this._modelId = null;
        this.actions = {
            playPause: () => {
                this._videoClipMenuMain.playPauseButtonHandler();
            },
            fastForward: () => {
                if (!this._videoClipMenuRecommended.isFocused) {
                    this._videoClipMenuMain.fastForwardButtonHandler();
                }
            },
            rewind: () => {
                if (!this._videoClipMenuRecommended.isFocused) {
                    this._videoClipMenuMain.rewindButtonHandler();
                }
            },
        };
        this.switchVisibility = () => {
            if (this._isDragging || !this._isActive)
                return;
            // Inspector:
            // Comment function body to work with the Inspector.
            if (VideoClipMenu._isVisible) {
                this.hide({
                    saveKeyboardState: true,
                    saveMainMenuPanelsState: true,
                    saveLoginDialogState: true,
                    saveInfoPanelState: true,
                    saveHelpPanelState: true,
                    saveMessengerPanelState: true,
                    saveMessengerChatListPanelState: true,
                });
            }
            else {
                this.show();
            }
        };
        this._videoClipMenuCallbacks = videoClipMenuCallbacks;
        this._projectionMeshCallbacks = projectionMeshCallbacks;
        const r = Settings.getInstance().broadcast.videoMenuRotation ?? Quaternion.FromEulerAngles(Env.videoClip.menu.position.xAngle * deg2rad, 0, 0);
        this._menuRootNode.rotationQuaternion = new Quaternion(r._x, r._y, r._z, r._w);
        const menu = Env.videoClip.menu;
        const menuCenterPosition = createVector3(menu.position.center);
        const mainPanelPos = this._getMeshPanelPosition(menuCenterPosition, menu.mainPanel.verticalOffset);
        this._menuMainMesh = this._createPanelMesh(MeshName.videoMenuMainPanel, menu.width, menu.mainPanel.height, mainPanelPos);
        const recommendedVideosPanelPos = this._getMeshPanelPosition(menuCenterPosition, menu.recommendedVideosPanel.verticalOffset);
        this._menuRecommendedVideosMesh = this._createPanelMesh(MeshName.videoMenuRecommendedVideosPanel, menu.width, menu.recommendedVideosPanel.height, recommendedVideosPanelPos);
        this._menuRecommendedVideosMesh.mesh.rotateAround(recommendedVideosPanelPos.add(new Vector3(0, menu.recommendedVideosPanel.height / 2 + menu.gapBetweenPanels / 2, 0)), Vector3.Right(), menu.recommendedVideosPanel.rotation * deg2rad);
        this._dragAndDropMainNode = new TransformNode("dragAndDropMainNode", this._menuRootNode._scene);
        this._dragAndDropMainNode.parent = this._menuMainMesh.mesh;
        this._dragAndDropMainNode.position = new Vector3(0, -menu.mainPanel.height / 2 - menu.dragAndDrop.gap - menu.dragAndDrop.height / 2, 0);
        this._dragAndDropRecommendedNode = new TransformNode("dragAndDropRecommendedNode", this._menuRootNode._scene);
        this._dragAndDropRecommendedNode.parent = this._menuRecommendedVideosMesh.mesh;
        this._dragAndDropRecommendedNode.position = new Vector3(0, -menu.recommendedVideosPanel.height / 2 - menu.dragAndDrop.gap - menu.dragAndDrop.height / 2, 0);
        // const ell = Env.broadcast.menu.navigation2.submenus.ellipsis;
        // this._submenuEllipsisMesh = this._createPanelMesh(MeshName.broadcastMenuSubmenuEllipsis,
        //     ell.width, ell.height, mainPanelPos.add(new Vector3(menu.submenus.ellipsis.offset.x, menu.submenus.ellipsis.offset.y, 0)),
        //     undefined, DcRenderingGroup.messageDialogs);
        const vol = Env.broadcast.menu.navigation.submenus.volume;
        this._submenuVolumeMesh = this._createPanelMesh(MeshName.broadcastMenuSubmenuVolume, vol.width, vol.height, mainPanelPos.add(new Vector3(menu.submenus.volume.offset.x, menu.submenus.volume.offset.y, 0)), undefined, DcRenderingGroup.messageDialogs);
        const cli = Env.broadcast.menu.user.submenus.clientProfile;
        this._submenuClientProfileMesh = this._createPanelMesh(MeshName.broadcastMenuSubmenuClientProfile, cli.width, cli.height, mainPanelPos.add(new Vector3(menu.submenus.clientProfile.offset.x, menu.submenus.clientProfile.offset.y, -0.01)), undefined, DcRenderingGroup.messageDialogs);
        const inf = menu.infoPanel;
        this._videoClipInfoPanelMesh = this._createPanelMesh(MeshName.videoMenuInfoPanel, inf.width, inf.height, mainPanelPos.add(new Vector3(0, //-menu.width/2 - menu.gapBetweenPanels - inf.width/2,
        -menu.mainPanel.height / 2 + inf.height / 2, 0)), undefined, DcRenderingGroup.messageDialogs, true, false);
        this._infoPanel = new VideoClipInfoPanel(this._videoClipInfoPanelMesh, {
            onShow: () => {
                setTimeout(() => {
                    this.setMenuActivity(false, excludedInfoPanelMeshNames);
                    this._videoClipMenuCallbacks.setLockedVideoCoverActivity(false, excludedInfoPanelMeshNames);
                }, 0);
            },
            onClose: () => {
                setTimeout(() => {
                    this._videoClipMenuMain.updateInfoButtonLayout();
                    this.setMenuActivity(true, []);
                    this._videoClipMenuCallbacks.setLockedVideoCoverActivity(true, excludedInfoPanelMeshNames);
                }, 0);
            },
            openBroadcastByModelNicknameAsync: async (modelNickname) => {
                this._videoClipMenuCallbacks.setLockedVideoCoverActivity(true, excludedInfoPanelMeshNames);
                this.setMenuActivity(true, []);
                await this._callbacks.openBroadcastAsync({ modelNickname });
            },
        });
        const hlp = menu.helpPanel;
        this._videoClipHelpPanelMesh = this._createPanelMesh(MeshName.videoMenuHelpPanel, hlp.width, hlp.height, mainPanelPos.add(new Vector3(0, -menu.mainPanel.height / 2 + inf.height / 2, 0)), undefined, DcRenderingGroup.messageDialogs, true, false);
        this._helpPanel = new VideoClipHelpPanel(this._videoClipHelpPanelMesh, {
            onShow: () => {
                setTimeout(() => {
                    this.setMenuActivity(false, excludedHelpPanelMeshNames);
                    this._videoClipMenuCallbacks.setLockedVideoCoverActivity(false, excludedHelpPanelMeshNames);
                }, 0);
            },
            onClose: () => {
                setTimeout(() => {
                    this.setMenuActivity(true, []);
                    this._videoClipMenuCallbacks.setLockedVideoCoverActivity(true, excludedHelpPanelMeshNames);
                }, 0);
            },
        });
        const dialogMessengerCallbacks = {
            showMessengerDialog: () => {
                this.setMenuActivity(false, excludedMessengerPanelMeshNames);
                this._videoClipMenuCallbacks.setLockedVideoCoverActivity(false, excludedMessengerPanelMeshNames);
                this._messengerPanel.show(() => {
                    this._videoClipMenuMain.isMessengerButtonOn = false;
                });
            },
            hideMessengerDialog: () => {
                this._messengerPanel.hide();
                this._messengerChatListPanel.hide();
                this.setMenuActivity(true, []);
                this._videoClipMenuCallbacks.setLockedVideoCoverActivity(true, excludedMessengerPanelMeshNames);
            },
        };
        this._videoClipMenuMain = new VideoClipMenuMain(this._menuMainMesh, 
        // this._submenuEllipsisMesh,
        this._submenuVolumeMesh, this._submenuClientProfileMesh, this._loginDialog, this._infoPanel, this._helpPanel, callbacks, dialogMessengerCallbacks, projectionMeshCallbacks);
        this._videoClipMenuRecommended = new VideoClipMenuRecommended(this._menuRecommendedVideosMesh, callbacks);
        this._messengerPanel = this._createMessengerPanel({
            showChatListPanel: () => {
                this._messengerChatListPanel?.show(() => {
                    this._messengerPanel.isChatListButtonOn = false;
                });
                this._messengerPanel.isChatListButtonOn = true;
            },
            hideChatListPanel: () => {
                this._messengerChatListPanel?.hide();
                this._messengerPanel.isChatListButtonOn = false;
            },
            viewModelProfile: () => { },
            showPrivateShowOptions: () => { },
            gotoLiveRoomAsync: async (modelNickname) => {
                await this._callbacks.openBroadcastAsync({ modelNickname: modelNickname, extraOptions: { openMessenger: true } });
            },
        });
        this._messengerChatListPanel = this._createMessengerChatListPanel();
        this.hide({
            saveKeyboardState: false,
            saveMainMenuPanelsState: false,
            saveLoginDialogState: false,
            saveInfoPanelState: false,
            saveHelpPanelState: false,
            saveMessengerPanelState: false,
            saveMessengerChatListPanelState: false,
        });
        VideoClipMenu._isVisible = true;
    }
    _getMessengerPanelMeshName() {
        return MeshName.videoClipMessengerPanel;
    }
    _getMessengerPanelPosition() {
        const menu = Env.videoClip.menu;
        const menuCenterPosition = createVector3(menu.position.center);
        const verticalOffset = menu.mainPanel.verticalOffset
            - menu.mainPanel.height / 2
            + Env.common.messengerPanel.background.height / 2;
        return this._getMeshPanelPosition(menuCenterPosition, verticalOffset);
    }
    _getMessengerChatListPanelMeshName() {
        return MeshName.videoClipMessengerChatListPanel;
    }
    _getMessengerChatListPanelPosition() {
        const offsetX = -Env.common.messengerPanel.background.width / 2
            - Env.broadcast.menu.gapBetweenPanels
            - Env.common.messengerChatListPanel.background.width / 2;
        return this._getMessengerPanelPosition()
            .add(new Vector3(offsetX, 0, 0));
    }
    _getMessengerEllipsisSubmenuMeshName() {
        return MeshName.videoClipMessengerEllipsisSubmenu;
    }
    _getMessengerLinkWarningDialogMeshName() {
        return MeshName.videoClipMessengerLinkWarningDialog;
    }
    _getKeyboardMeshName() {
        return MeshName.videoClipChatKeyboard;
    }
    _getKeyboardPosition() {
        const menu = Env.videoClip.menu;
        const menuCenterPosition = createVector3(menu.position.center);
        const keyboardOffset = -Env.common.messengerPanel.background.height / 2 - menu.gapBetweenPanels - Env.keyboard.height / 2;
        return this._getMeshPanelPosition(menuCenterPosition, keyboardOffset);
    }
    _getHideKeyboardMeshName() {
        return MeshName.hideVideoClipChatKeyboard;
    }
    _getMessengerTipsKeyboardMeshName() {
        return MeshName.videoClipMessengerTipsKeyboardPanel;
    }
    _createDragAndDropMesh() {
        return this._createPanelMesh(MeshName.videoClipMenuDragAndDropControl, Env.videoClip.menu.dragAndDrop.width, Env.videoClip.menu.dragAndDrop.height, Vector3.Zero(), undefined, DcRenderingGroup.dialogs, false);
    }
    setMenuActivity(isActive, excludedMeshNames) {
        this._isActive = isActive;
        this._menuMainMesh.mesh.isPickable = isActive;
        this._menuRecommendedVideosMesh.mesh.isPickable = isActive;
        Mesh2D.Meshes
            .filter(m => excludedMeshNames.find(x => m.name === x) === undefined)
            .forEach(m => m.mesh.colorMultiplier = isActive ? 1.0 : 0.4);
        this._projectionMeshCallbacks.setColorMultiplier(isActive ? 1.0 : 0.4);
        this._dragAndDropMesh.mesh.isPickable = isActive;
    }
    _getMenuActivity() {
        return this._isActive;
    }
    show() {
        if (this._videoClip === null /*|| (!this._videoClip.isFree && this._videoClip.product?.isPurchased !== true)*/) {
            this.hide({
                saveKeyboardState: false,
                saveMainMenuPanelsState: false,
                saveLoginDialogState: false,
                saveInfoPanelState: false,
                saveHelpPanelState: false,
                saveMessengerPanelState: false,
                saveMessengerChatListPanelState: false,
            });
            return;
        }
        Settings.getInstance().broadcast.showHelp = false;
        const r = Settings.getInstance().broadcast.videoMenuRotation ?? Quaternion.FromEulerAngles(Env.videoClip.menu.position.xAngle * deg2rad, 0, 0);
        this._menuRootNode.rotationQuaternion = new Quaternion(r._x, r._y, r._z, r._w);
        AppView.getInstance().updateHeartsAnimationPosition(this._menuRootNode);
        VideoClipMenu._isVisible = true;
        if (this._loginDialog && VideoClipMenu._loginDialogVisibleOnHide === true) {
            this._loginDialog.show();
        }
        if (VideoClipMenu._infoPanelVisibleOnHide === true) {
            this._infoPanel.show();
        }
        if (VideoClipMenu._helpPanelVisibleOnHide === true) {
            this._helpPanel.show();
        }
        if (VideoClipMenu._messengerVisibleOnHide !== undefined) {
            this._messengerPanel.isVisible = VideoClipMenu._messengerVisibleOnHide;
        }
        if (VideoClipMenu._messengerChatListVisibleOnHide !== undefined) {
            this._messengerChatListPanel.isVisible = VideoClipMenu._messengerChatListVisibleOnHide;
        }
        if (VideoClipMenu._keyboardVisibleOnHide !== undefined) {
            this._keyboard.isVisible = VideoClipMenu._keyboardVisibleOnHide;
        }
        if (VideoClipMenu._mainMenuPanelsVisibleOnHide !== false) { // (undefined or true)
            this._showMainMenuPanels();
        }
    }
    hide(params) {
        if (this._loginDialog) {
            VideoClipMenu._loginDialogVisibleOnHide = params.saveLoginDialogState ? this._loginDialog.isVisible : undefined;
            this._loginDialog.hide();
        }
        VideoClipMenu._keyboardVisibleOnHide = params.saveKeyboardState ? this._keyboard.isVisible : undefined;
        this._keyboard.isVisible = false;
        VideoClipMenu._infoPanelVisibleOnHide = params.saveInfoPanelState ? this._infoPanel.isVisible : undefined;
        this._infoPanel.hide();
        VideoClipMenu._helpPanelVisibleOnHide = params.saveHelpPanelState ? this._helpPanel.isVisible : undefined;
        this._helpPanel.hide();
        VideoClipMenu._mainMenuPanelsVisibleOnHide = params.saveMainMenuPanelsState ? this._videoClipMenuMain.isVisible : undefined;
        this._hideMainMenuPanels();
        VideoClipMenu._messengerChatListVisibleOnHide = params.saveMessengerChatListPanelState ? this._messengerChatListPanel.isVisible : undefined;
        this._messengerChatListPanel.isVisible = false;
        VideoClipMenu._messengerVisibleOnHide = params.saveMessengerPanelState ? this._messengerPanel.isVisible : undefined;
        this._messengerPanel.isVisible = false;
        VideoClipMenu._isVisible = false;
        this.setMenuActivity(true, []);
    }
    _hideMainMenuPanels() {
        this._hideInternal();
        this._videoClipMenuMain.hide();
        this._videoClipMenuRecommended.hide();
    }
    _showMainMenuPanels() {
        this._showInternal();
        this._videoClipMenuMain.show();
        this._videoClipMenuRecommended.show();
    }
    _onDragEnded() {
        super._onDragEnded();
        Settings.getInstance().broadcast.videoMenuRotation = this._menuRootNode.rotationQuaternion;
        AppView.getInstance().updateHeartsAnimationPosition(this._menuRootNode);
        this.setMenuActivity(this._isActive, excludedLoginDialogMeshNames);
    }
    _onShowLoginDialog() {
        super._onShowLoginDialog();
        setTimeout(() => {
            this._videoClipMenuCallbacks.setLockedVideoCoverActivity(false, excludedLoginDialogMeshNames);
            this.setMenuActivity(false, excludedLoginDialogMeshNames);
        }, 0);
    }
    _onCloseLoginDialog() {
        super._onCloseLoginDialog();
        setTimeout(() => {
            this.setMenuActivity(true, []);
            this._videoClipMenuCallbacks.setLockedVideoCoverActivity(true, excludedLoginDialogMeshNames);
        }, 0);
    }
    stateChanged(state, isGuest, reason) {
        this._videoClipMenuMain.stateChanged(state, isGuest, reason);
        this._infoPanel.stateChanged(state, isGuest, reason);
        this._messengerPanel.outputPinnedBroadcastManagerState(state, isGuest, reason);
        this._messengerChatListPanel.outputPinnedBroadcastManagerState(state, isGuest, reason);
        if (this.loginDialog?.isVisible === true) {
            this.setMenuActivity(false, excludedLoginDialogMeshNames);
        }
        else if (this._videoClipHelpPanelMesh.isVisible) {
            this.setMenuActivity(false, excludedHelpPanelMeshNames);
        }
        else if (this._videoClipInfoPanelMesh.isVisible) {
            this.setMenuActivity(false, excludedInfoPanelMeshNames);
        }
        else if (this._messengerPanel.isVisible) {
            this.setMenuActivity(false, excludedMessengerPanelMeshNames);
        }
    }
    outputVideoClip(videoClip) {
        this._videoClip = videoClip;
        this._videoClipMenuMain.outputVideoClip(videoClip);
        this._videoClipMenuRecommended.outputVideoClip(videoClip);
        this._infoPanel.outputVideoClip(videoClip);
        if (videoClip === null) {
            this.hide({
                saveKeyboardState: false,
                saveMainMenuPanelsState: false,
                saveLoginDialogState: false,
                saveInfoPanelState: false,
                saveHelpPanelState: false,
                saveMessengerPanelState: false,
                saveMessengerChatListPanelState: false,
            });
        }
    }
    outputRecommendedVideoClips(videoClips) {
        // videoClips.pageItems.length = 0; // (to check empty recommendations)
        this._videoClipMenuRecommended.outputRecommendedVideoClips(videoClips);
        this._dragAndDropMesh.mesh.parent = videoClips.pageItems.length > 0
            ? this._dragAndDropRecommendedNode
            : this._dragAndDropMainNode;
    }
    outputClientInfo(isGuest) {
        this._videoClipMenuMain.outputClientInfo(isGuest);
    }
    outputClientNickname(nickname) {
        this._videoClipMenuMain.outputClientNickname(nickname);
    }
    outputClientBalance(tokens) {
        this._videoClipMenuMain.outputClientBalance(tokens);
    }
    async outputModelAsync(id, nickname) {
        this._modelId = id;
        this._messengerPanel.setPinnedModelId(id);
        await this._messengerChatListPanel.setPinnedModelIdAsync(id);
        this._messengerChatListPanel.setSelectedModelId(id);
        this._messengerPanel.outputPinnedModelNickname(id, nickname);
        this._messengerChatListPanel.outputPinnedModelNickname(id, nickname);
    }
    outputIsFavorite(modelId, isFavorite) {
        if (modelId !== null) {
            if (modelId === this._modelId) {
                // this._videoClipMenuMain.outputIsFavorite(isFavorite);
                this._messengerChatListPanel.outputPinnedIsFavorite(isFavorite);
            }
            this._messengerPanel?.outputMessengerIsFavoriteChanged(modelId, isFavorite);
            this._messengerChatListPanel?.outputMessengerIsFavoriteChanged(modelId, isFavorite);
        }
    }
    overwriteIsFavorite(modelId, isFavorite) {
        this._messengerPanel?.overwriteIsFavorite(modelId, isFavorite);
    }
    outputLastBroadcast(value) {
        this._messengerPanel.outputPinnedLastBroadcast(value);
    }
    setModelProfilePhoto(url) {
        this._messengerPanel.outputPinnedProfilePhoto(url);
        this._messengerChatListPanel.outputPinnedProfilePhoto(url);
    }
    outputModelProfile(_modelId, profile, filter, hasVideoClips) {
        this._infoPanel.outputModelProfile(profile, filter, hasVideoClips);
    }
    outputMessengerModelStatuses(modelStatuses) {
        this._messengerPanel?.outputMessengerModelStatuses(modelStatuses);
        this._messengerChatListPanel?.outputMessengerModelStatuses(modelStatuses);
    }
    outputMessengerSelectedModel(modelId, chat) {
        this._messengerChatListPanel?.outputMessengerSelectedModel(modelId);
        this._messengerPanel?.outputMessengerSelectedModel(modelId, chat);
    }
    outputMessengerLoggedState(isAuthorized) {
        this._videoClipMenuMain.outputMessengerLoggedState(isAuthorized);
    }
    outputUnreadMessagesCount(unreadChats) {
        this._videoClipMenuMain.outputUnreadMessagesCount(unreadChats);
        this._messengerPanel?.outputUnreadMessagesCount(unreadChats);
    }
    setMessengerDataCallbacks(callbacks) {
        this._messengerCallbacks = callbacks;
        this._messengerPanel?.setMessengerDataCallbacks(callbacks);
        this._messengerChatListPanel?.setMessengerDataCallbacks(callbacks);
    }
    outputMessengerChats(chatList) {
        // this._messengerPanel.outputMessengerChats(chatList);
        this._messengerChatListPanel.outputMessengerChats(chatList);
    }
    outputMessengerChatChangedForChatList(args) {
        this._messengerChatListPanel.outputMessengerChatChanged(args);
    }
    outputMessengerChatChangedForMessages(args) {
        this._messengerPanel.outputMessengerChatChanged(args);
    }
    outputMessengerModelTypingChanged(chatId, isTyping) {
        this._messengerPanel.outputMessengerModelTypingChanged(chatId, isTyping);
        this._messengerChatListPanel.outputMessengerModelTypingChanged(chatId, isTyping);
    }
    outputMessengerMessages(chatId, messageListByDate) {
        this._messengerPanel.outputMessengerMessages(chatId, messageListByDate);
    }
    setMessengerInputControlsAvailability(params) {
        this._messengerPanel.setInputControlsAvailability(params);
    }
    closeMessenger() {
        this._videoClipMenuMain.isMessengerButtonOn = false;
    }
}
